import type { ScaleWeightData } from "@blackfisk/vue/composables/useScale";
import type { ComputedRef, Ref, ShallowRef } from "vue";

import { useLogger } from "@blackfisk/vue/composables/useLogger";
import { useScale } from "@blackfisk/vue/composables/useScale";
import { useThrottleFn } from "@vueuse/core";
import { ref } from "vue";

const scaleUnits = ref<ScaleWeightData["units"]>("lb");
const scaleWeight = ref<ScaleWeightData["value"]>(0);
const isReadingScale = ref(false);
const debug = import.meta.env.VITE_ENVIRONMENT === "local";
const logger = useLogger("client-frontend", "scale");

export const useScaleWeight = (): {
  isScalePaired: ComputedRef<boolean>;
  isScaleOpened: Ref<boolean, boolean>;
  scaleUnits: Ref<"kg" | "lb", "kg" | "lb">;
  scaleWeight: Ref<number, number>;
  isReadingScale: Ref<boolean, boolean>;
  requestScale: () => Promise<boolean>;
  updateScale: () => Promise<void>;
  scaleDevice: ShallowRef<HIDDevice | null, HIDDevice | null>;
  open: () => Promise<void>;
  close: () => Promise<void>;
} => {
  const {
    updateScale,
    isScalePaired,
    isScaleOpened,
    requestScale,
    connectScale,
    initiateRead,
    closeScale,
    openScale,
    scaleDevice,
  } = useScale(
    (weight) => {
      if (weight.state === "settled") {
        scaleUnits.value = weight.units;
        scaleWeight.value = weight.value;
      } else {
        throttledScaleReading(weight);
      }
    },
    (event) => {
      if (debug) {
        logger.debug("Connected Scale: {name}", { name: event.device.productName });
      }
      open();
    },
    (event) => {
      if (debug) {
        logger.debug("Disconnected Scale: {name}", { name: event.device.productName });
      }
      reset();
    },
    debug,
  );
  const throttledScaleReading = useThrottleFn((weight: ScaleWeightData) => {
    scaleUnits.value = weight.units;
    scaleWeight.value = weight.value;
  }, 250);
  const reset = (): void => {
    scaleUnits.value = "lb";
    scaleWeight.value = 0;
    isReadingScale.value = false;
  };
  const open = async (): Promise<void> => {
    await updateScale();
    if (!isScalePaired.value) {
      if (!isScaleOpened.value) {
        await openScale();
      }
      try {
        await connectScale();
        await initiateRead();
        if (isScaleOpened.value) {
          isReadingScale.value = true;
        }
      } catch (err) {
        logger.error("Operation failed", { error: String(err) });
        await closeScale();
      }
    } else {
      if (!isScaleOpened.value) {
        await openScale();
      }
      await initiateRead();
      if (isScaleOpened.value) {
        isReadingScale.value = true;
      }
      isReadingScale.value = true;
    }
  };
  const close = async (): Promise<void> => {
    await closeScale();
    reset();
  };
  return {
    isScalePaired,
    isScaleOpened,
    scaleUnits,
    scaleWeight,
    isReadingScale,
    requestScale,
    updateScale,
    scaleDevice,
    open,
    close,
  };
};
